import { useEffect, useState, useCallback, useRef } from "react";
import { getWithCache, getWithCacheETag, clearCache } from "./apiCache";
import { TokenService } from "./tokenService";

export interface UseApiCacheOptions<T> {
	/** Tempo de vida do cache em milissegundos */
	ttlMs?: number;
	/** Área de armazenamento do cache */
	area?: "session" | "local";
	/** Função de validação dos dados */
	validate?: (data: T) => boolean;
	/** Se true, sempre busca dados frescos (ignora cache) - NÃO usar com useETag */
	skipCache?: boolean;
	/** Se true, NÃO carrega automaticamente no mount (carregamento sob demanda) */
	lazy?: boolean;
	/** Número máximo de tentativas em caso de erro */
	maxRetries?: number;
	/** Delay entre tentativas em ms */
	retryDelayMs?: number;
	/**
	 * Se true, usa cache baseado em ETag HTTP (recomendado para ferramentas).
	 * - Envia If-None-Match com ETag do cache
	 * - Se servidor retorna 304, usa dados do cache local
	 * - Se servidor retorna 200, atualiza cache com novos dados
	 * NOTA: Não compatível com skipCache (use forceRefresh via refresh())
	 */
	useETag?: boolean;
}

export interface UseApiCacheResult<T> {
	/** Dados retornados pela API */
	data: T | null;
	/** Indica se está carregando */
	loading: boolean;
	/** Mensagem de erro (null se não houver erro) */
	error: string | null;
	/** Código do erro para tratamento específico */
	errorCode: "network" | "validation" | "auth" | "unknown" | null;
	/** Força refresh dos dados */
	refresh: () => Promise<void>;
	/** Recarrega os dados (com opção de forçar) */
	reload: (forceRefresh?: boolean) => Promise<void>;
	/** Limpa o cache */
	clear: () => void;
	/** Indica se já tentou carregar pelo menos uma vez */
	hasAttempted: boolean;
	/** Indica se os dados vieram do cache (304 Not Modified) - só disponível com useETag */
	fromCache?: boolean;
	/** ETag atual dos dados - só disponível com useETag */
	etag?: string;
}

/**
 * Hook para buscar dados da API com cache inteligente.
 *
 * Utiliza o padrão de "refs para opções" para evitar loops infinitos
 * causados por objetos/funções inline nas dependências.
 *
 * @example
 * // Uso básico
 * const { data, loading, error, refresh } = useApiCache<User[]>(
 *   'users-list',
 *   '/users'
 * );
 *
 * @example
 * // Com opções (pode usar inline sem causar loops)
 * const { data } = useApiCache<Config>(
 *   'config',
 *   '/config',
 *   {
 *     ttlMs: 5 * 60 * 1000,
 *     validate: (data) => !!data.version  // ← Seguro passar inline!
 *   }
 * );
 */
export function useApiCache<T = any>(
	key: string,
	endpoint: string,
	opts?: UseApiCacheOptions<T>,
): UseApiCacheResult<T> {
	// ═══════════════════════════════════════════════════════════════════════════
	// ESTADO
	// ═══════════════════════════════════════════════════════════════════════════
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [errorCode, setErrorCode] = useState<
		"network" | "validation" | "auth" | "unknown" | null
	>(null);
	const [hasAttempted, setHasAttempted] = useState<boolean>(false);
	const [fromCache, setFromCache] = useState<boolean | undefined>(undefined);
	const [etag, setEtag] = useState<string | undefined>(undefined);

	// ═══════════════════════════════════════════════════════════════════════════
	// REFS (evitam loops infinitos de dependências)
	// ═══════════════════════════════════════════════════════════════════════════

	/** Ref para controle de loading (evita requisições simultâneas) */
	const loadingRef = useRef(false);

	/** Ref para verificar se componente está montado */
	const mountedRef = useRef(true);

	/**
	 * Ref para armazenar opções (PADRÃO ARQUITETURAL)
	 *
	 * Isso permite passar objetos/funções inline sem causar re-renders:
	 * - A ref é atualizada a cada render com as opções mais recentes
	 * - O useCallback não depende das opções (apenas da ref)
	 * - Dentro do callback, acessamos optsRef.current (sempre atualizado)
	 */
	const optsRef = useRef<UseApiCacheOptions<T> | undefined>(opts);

	/** Ref para key (para uso no clear callback) */
	const keyRef = useRef(key);

	// ═══════════════════════════════════════════════════════════════════════════
	// ATUALIZAÇÃO DAS REFS A CADA RENDER
	// ═══════════════════════════════════════════════════════════════════════════

	// Atualiza as refs com valores mais recentes (executado a cada render)
	optsRef.current = opts;
	keyRef.current = key;

	// ═══════════════════════════════════════════════════════════════════════════
	// LIFECYCLE
	// ═══════════════════════════════════════════════════════════════════════════

	// Cleanup ao desmontar
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// ═══════════════════════════════════════════════════════════════════════════
	// FUNÇÃO DE CARREGAMENTO (ESTÁVEL)
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Carrega dados da API com retry e cache.
	 *
	 * NOTA: Dependências são apenas [key, endpoint] (strings estáveis).
	 * As opções são acessadas via optsRef.current (sempre atualizado).
	 */
	const load = useCallback(
		async (forceRefresh = false) => {
			// Evitar requisições simultâneas
			if (loadingRef.current) return;

			// Acessar opções via ref (sempre o valor mais recente)
			const currentOpts = optsRef.current;

			// Verificar se há token antes de fazer requisição
			const hasToken = !!TokenService.getAccessToken();

			if (!hasToken) {
				// Não fazer requisição se não houver token
				if (mountedRef.current) {
					setError("Autenticação necessária");
					setErrorCode("auth");
					setHasAttempted(true);
				}
				return;
			}

			loadingRef.current = true;
			if (mountedRef.current) {
				setLoading(true);
				setError(null);
				setErrorCode(null);
			}

			// Usar valores das opções via ref
			const maxRetries = currentOpts?.maxRetries ?? 2;
			const baseRetryDelayMs = currentOpts?.retryDelayMs ?? 500;
			const area = currentOpts?.area || "session";
			const useETagMode = currentOpts?.useETag ?? false;

			let lastError: any = null;

			for (let attempt = 0; attempt < maxRetries; attempt++) {
				try {
					// ═══════════════════════════════════════════════════════════════
					// MODO ETAG: usa getWithCacheETag (recomendado para ferramentas)
					// ═══════════════════════════════════════════════════════════════
					if (useETagMode) {
						const etagResult = await getWithCacheETag<T>({
							key,
							endpoint,
							ttlMs: currentOpts?.ttlMs,
							area: currentOpts?.area,
							validate: currentOpts?.validate,
							forceRefresh: forceRefresh || attempt > 0,
						});

						if (mountedRef.current) {
							setData(etagResult.data);
							setFromCache(etagResult.fromCache);
							setEtag(etagResult.etag);
							setError(null);
							setErrorCode(null);
							setHasAttempted(true);
						}

						loadingRef.current = false;
						if (mountedRef.current) {
							setLoading(false);
						}
						return;
					}

					// ═══════════════════════════════════════════════════════════════
					// MODO TRADICIONAL: usa getWithCache (compatibilidade)
					// ═══════════════════════════════════════════════════════════════
					if (forceRefresh || attempt > 0) {
						clearCache(key, area);
					}

					const result = await getWithCache<T>({
						key,
						endpoint,
						ttlMs: currentOpts?.ttlMs,
						area: currentOpts?.area,
						validate: currentOpts?.validate,
						skipCache: currentOpts?.skipCache,
					});

					if (mountedRef.current) {
						setData(result);
						setError(null);
						setErrorCode(null);
						setHasAttempted(true);
					}

					loadingRef.current = false;
					if (mountedRef.current) {
						setLoading(false);
					}
					return;
				} catch (e: any) {
					lastError = e;

					// ✅ OTIMIZADO: Backoff exponencial para retry
					// Se não é a última tentativa, aguardar antes de retry
					if (attempt < maxRetries - 1) {
						// Backoff exponencial: delay = baseDelay * 2^attempt
						const exponentialDelay = baseRetryDelayMs * Math.pow(2, attempt);
						await new Promise((resolve) =>
							setTimeout(resolve, exponentialDelay),
						);
					}
				}
			}

			// Todas as tentativas falharam
			if (mountedRef.current) {
				const errorMessage = lastError?.message || String(lastError);
				setError(errorMessage);
				setHasAttempted(true);

				// Classificar o tipo de erro
				if (
					errorMessage.includes("network") ||
					errorMessage.includes("fetch") ||
					errorMessage.includes("Network")
				) {
					setErrorCode("network");
				} else if (
					errorMessage.includes("validat") ||
					errorMessage.includes("invalid")
				) {
					setErrorCode("validation");
				} else if (
					errorMessage.includes("401") ||
					errorMessage.includes("auth") ||
					errorMessage.includes("token")
				) {
					setErrorCode("auth");
				} else {
					setErrorCode("unknown");
				}
			}

			loadingRef.current = false;
			if (mountedRef.current) {
				setLoading(false);
			}
		},
		[key, endpoint],
	); // ← Apenas dependências estáveis (strings)!

	// ═══════════════════════════════════════════════════════════════════════════
	// AUTO-LOAD NO MOUNT
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Carrega automaticamente quando:
	 * - Componente monta
	 * - key ou endpoint mudam
	 *
	 * NÃO carrega se lazy: true
	 */
	useEffect(() => {
		// Evita exibir dados da chave anterior enquanto carrega nova categoria/endpoint
		setData(null);
		setError(null);
		setErrorCode(null);
		setHasAttempted(false);
		setFromCache(undefined);
		setEtag(undefined);

		// Verificar lazy via ref para não causar re-execução
		if (!optsRef.current?.lazy) {
			load(false);
		}
	}, [load]); // ← load é estável (só muda se key/endpoint mudam)

	// ═══════════════════════════════════════════════════════════════════════════
	// CALLBACKS EXPOSTOS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Força refresh (ignora cache) */
	const refresh = useCallback(() => load(true), [load]);

	/** Limpa o cache manualmente */
	const clear = useCallback(() => {
		clearCache(keyRef.current, optsRef.current?.area || "session");
	}, []); // ← Estável, usa refs

	// ═══════════════════════════════════════════════════════════════════════════
	// RETORNO
	// ═══════════════════════════════════════════════════════════════════════════

	return {
		data,
		loading,
		error,
		errorCode,
		refresh,
		reload: load,
		clear,
		hasAttempted,
		fromCache,
		etag,
	};
}
